import {
  Autocomplete,
  Button,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  type ButtonTypeMap,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState, type FC } from "react";
// Replace read-excel-file with xlsx
import * as XLSX from "xlsx";
import { remult } from "remult";
import { Roles } from "@shared/Roles";
import { type Association, type AssociationTeam, type Organization, type Team, type User, type UserSkill } from "@entities";
import { associationRepo, associationTeamRepo, organizationRepo, skillRepo, teamRepo, userRepo, userSkillRepo } from "@repositories";

type ButtonProps = ButtonTypeMap<{}, "button">["props"];

interface ExcelButtonProps extends ButtonProps {
  disabled?: true;
}

// Define Row as an array of any values
type Row = any[];

const ORG_SEARCH_LIMIT = 30;
const ORG_EMPTY_PAGE_LIMIT = 200;
const COMMUNITIES_LIMIT = 200;

export const ExcelButton: FC<ExcelButtonProps> = (props) => {
  const [selectedOrganization, setSelectedOrganization] = useState<string>();
  const [selectedOrgOption, setSelectedOrgOption] = useState<Organization | null>(null);
  const [orgOptions, setOrgOptions] = useState<Organization[]>([]);
  const [orgInputValue, setOrgInputValue] = useState("");
  const [orgLoading, setOrgLoading] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const [excelData, setExcelData] = useState<Row[] | null>(null);
  const [communities, setCommunities] = useState<Team[]>([]);
  const [selectedCommunity, setSelectedCommunity] = useState<string>();
  const { disabled, ...buttonProps } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const searchOrganizations = useCallback(async (query: string) => {
    const q = query.trim();
    setOrgLoading(true);
    try {
      if (q.length < 1) {
        const list = await organizationRepo.find({
          orderBy: { name: "asc" },
          limit: ORG_EMPTY_PAGE_LIMIT,
        });
        setOrgOptions(list);
        return;
      }
      const list = await organizationRepo.find({
        where: { name: { $contains: q } },
        orderBy: { name: "asc" },
        limit: ORG_SEARCH_LIMIT,
      });
      setOrgOptions(list);
    } finally {
      setOrgLoading(false);
    }
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void searchOrganizations(orgInputValue);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgInputValue, searchOrganizations]);

  useEffect(() => {
    setSelectedCommunity(undefined);
    if (!selectedOrganization) {
      setCommunities([]);
      return;
    }

    let cancelled = false;
    const loadCommunities = async () => {
      const [mainTeams, restTeams] = await Promise.all([
        teamRepo.find({
          where: {
            organization: { $id: selectedOrganization },
            isMainOrganizationTeam: true,
          },
          limit: 1,
        }),
        teamRepo.find({
          where: {
            organization: { $id: selectedOrganization },
            isMainOrganizationTeam: false,
          },
          orderBy: { name: "asc" },
          limit: COMMUNITIES_LIMIT,
        }),
      ]);
      if (cancelled) return;
      const seen = new Set<string>();
      const merged: Team[] = [];
      for (const team of [...mainTeams, ...restTeams]) {
        if (seen.has(team.id)) continue;
        seen.add(team.id);
        merged.push(team);
      }
      setCommunities(merged);
    };
    void loadCommunities();
    return () => {
      cancelled = true;
    };
  }, [selectedOrganization]);

  const autocompleteOptions = useMemo(() => {
    if (selectedOrgOption && !orgOptions.some((org) => org.id === selectedOrgOption.id)) {
      return [selectedOrgOption, ...orgOptions];
    }
    return orgOptions;
  }, [orgOptions, selectedOrgOption]);

  async function handleExcelReader(event: React.ChangeEvent<HTMLInputElement>) {
    setFileName(event.target.files?.[0].name ?? "");
    if (!event.target.files?.[0]) return;

    try {
      const file = event.target.files[0];
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0]; // Read the first sheet
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json<Row[]>(sheet, { header: 1 }); // Convert to 2D array
      setExcelData(jsonData);
    } catch (error) {
      console.error("Error reading Excel file:", error);
      alert("Failed to read the Excel file. Please ensure the file format is supported.");
    }
  }

  async function uploadToDB() {
    if (!selectedOrganization || !excelData || isLoading) return;
    setIsLoading(true);
    const [columns, ...rows] = excelData;
    const basicCommunity = communities.find((community) => community.isMainOrganizationTeam)?.id;
    const usersSkills: { phoneNumber: string, skills: string[]; }[] = [];
    const skills = await skillRepo.find();
    const allUsers: User[] = [];
    let rowDataObjects: User[] = rows.map((row) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- temporary
      const rowDataObject: any = {};
      columns.forEach((column, index) => {
        if (column === "dateOfBirth")
          rowDataObject[column] = row[index] ? new Date((row[index]) as string) : new Date("2000-01-01");
        else if (column === "phoneNumber")
          rowDataObject[column] = "+972" + (row[index] as string)?.slice(1, 10);
        else if (column === "gender")
          rowDataObject[column] = row[index] || "";
        else if (column === "numberID") {
          rowDataObject[column] = row[index] && String(row[index])?.length === 9 ? String(row[index]) : "";
        }
        else if (column === "hasVehicle")
          rowDataObject[column] = row[index] || false;
        else rowDataObject[column as string] = row[index] || "";
      });
      if (rowDataObject.skills) {
        const userSkills: string[] = rowDataObject.skills.split(",").map((skill: string) => {
          const foundSkill = skills.find((s) => s.name === skill);
          if (foundSkill) {
            return foundSkill.id;
          }
          return "";
        });
        usersSkills.push({ phoneNumber: rowDataObject.phoneNumber, skills: userSkills.filter(skill => !!skill) });
        delete rowDataObject.skills;
      }
      return {
        ...rowDataObject,
      } as User;
    });

    const uniquePhoneNumbers = new Map<string, User>();

    for (const rowDataObject of rowDataObjects) {
      if (!uniquePhoneNumbers.has(rowDataObject.phoneNumber)) {
        uniquePhoneNumbers.set(rowDataObject.phoneNumber, rowDataObject);
      }
    }

    // Convert the Map values back to an array;
    rowDataObjects = Array.from(uniquePhoneNumbers.values());
    const batchSize = 100;
    const totalUsers = rowDataObjects.length;
    const numBatches = Math.ceil(totalUsers / batchSize);

    for (let i = 0; i < numBatches; i++) {
      const startIdx = i * batchSize;
      const endIdx = Math.min((i + 1) * batchSize, totalUsers);
      const batchData = rowDataObjects.slice(startIdx, endIdx);
      const batchPhones = batchData.map((user) => user.phoneNumber);

      const alreadyUsers = await userRepo.find({
        where: { phoneNumber: { $in: batchPhones } },
      });
      const alreadyUserIds = alreadyUsers.map((user) => user.id);
      const existingOrgAssociations = alreadyUserIds.length
        ? await associationRepo.find({
          where: {
            userId: { $in: alreadyUserIds },
            organizationId: selectedOrganization,
          },
        })
        : [];
      const alreadyInSelectedOrg = new Set(
        existingOrgAssociations.map((association) => association.userId),
      );

      const filteredBatchData = batchData.filter(
        (user) => !alreadyUsers.some((alreadyUser) => alreadyUser?.phoneNumber === user.phoneNumber)
      );
      const users = filteredBatchData.length
        ? await userRepo.insert(filteredBatchData)
        : [];
      allUsers.push(...users);

      const organizationRelations: Partial<Association>[] = [...users, ...alreadyUsers]
        .filter((user) => !alreadyInSelectedOrg.has(user.id))
        .map((user) => ({
          userId: user.id,
          organizationId: selectedOrganization,
          activityStatus: "ACTIVE",
        }));
      const associations = organizationRelations.length
        ? await associationRepo.insert(organizationRelations)
        : [];

      const basicCommunityRelations: Partial<AssociationTeam>[] = associations.map((association) => ({
        associationId: association.id,
        teamId: basicCommunity,
      }));

      let communityRelations: Partial<AssociationTeam>[] = [];

      if (selectedCommunity) {
        communityRelations = associations.map((association) => ({
          associationId: association.id,
          teamId: selectedCommunity,
        }));

        const existingForCommunity = existingOrgAssociations.filter((association) =>
          alreadyInSelectedOrg.has(association.userId),
        );
        if (existingForCommunity.length) {
          const existingCommunityLinks = await associationTeamRepo.find({
            where: {
              associationId: {
                $in: existingForCommunity.map((association) => association.id),
              },
              teamId: selectedCommunity,
            },
          });
          const hasSelectedCommunity = new Set(
            existingCommunityLinks.map((link) => link.associationId),
          );
          communityRelations.push(
            ...existingForCommunity
              .filter((association) => !hasSelectedCommunity.has(association.id))
              .map((association) => ({
                associationId: association.id,
                teamId: selectedCommunity,
              })),
          );
        }
      }

      const teamRelations = [...basicCommunityRelations, ...communityRelations];
      if (teamRelations.length) {
        await associationTeamRepo.insert(teamRelations);
      }
    }

    const skillsRelations: UserSkill[] = [];
    for (const { phoneNumber, skills } of usersSkills) {
      const user = allUsers.find((user) => user.phoneNumber === phoneNumber);
      if (user) {
        const userSkills = skills.map((skill) => ({ userId: user.id, skillId: skill }));
        skillsRelations.push(...userSkills);
      }
    }
    if (skillsRelations.length) {
      await userSkillRepo.insert(skillsRelations);
    }
    setIsLoading(false);
  }

  if (!remult.isAllowed(Roles.SuperDuperAdmin)) return null;

  return (
    <Stack alignItems="center" gap="2vh">
      <InputLabel>לאיזה ארגון להעלות את המתנדבים?</InputLabel>
      <Autocomplete
        options={autocompleteOptions}
        value={selectedOrgOption}
        onChange={(_, org) => {
          setSelectedOrgOption(org);
          setSelectedOrganization(org?.id);
        }}
        inputValue={orgInputValue}
        onInputChange={(_, value, reason) => {
          if (reason === "reset" && selectedOrgOption) {
            setOrgInputValue(selectedOrgOption.name);
            return;
          }
          setOrgInputValue(value);
        }}
        getOptionLabel={(org) => org.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={orgLoading}
        noOptionsText="הקלד לחיפוש ארגון"
        sx={{ width: "15rem" }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="-בחירת ארגון-"
          />
        )}
      />
      {selectedOrganization && <>
        <InputLabel>לאיזו פרויקט להעלות את המתנדבים? </InputLabel>
        <Select
          placeholder="-בחירת פרויקט-"
          MenuProps={{
            disableScrollLock: true,
          }}
          sx={{ width: "15rem" }}
          value={selectedCommunity || ""}
          onChange={(event) => {
            setSelectedCommunity(event.target.value);
          }}
        >
          {communities.map((community) => (
            <MenuItem key={community.id} value={community.id}>
              <ListItemText primary={community.name} />
            </MenuItem>
          ))}
        </Select>
      </>}
      {selectedOrganization && fileName && (
        <Button onClick={uploadToDB} variant="contained">
          העלאת המתנדבים למאגר המידע
        </Button>
      )}
      <Button variant="outlined" disabled={disabled} {...buttonProps}>
        <label htmlFor="excel-input">העלאת קובץ אקסל</label>
      </Button>
      {fileName}
      {!disabled && (
        <input
          style={{ display: "none" }}
          type="file"
          id="excel-input"
          onChange={handleExcelReader}
        />
      )}
      {isLoading && (
        <Typography sx={{ color: "error.main" }}>מעלה מתנדבים...</Typography>
      )}
    </Stack>
  );
};
